/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
'use client';

import '@/ux/features/website-assembler/blog/blog-list-atmospheric.css';
import './blog-card-overrides.css';

import { MessageCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import { ContentItem } from '@/core/ambient-intelligence/types/ambient-intelligence.types';
import { metadataCache } from '@/core/ambient-intelligence/utils/MetadataCache';
import { getBoardDataById, getMetaData } from '@/core/apis/bentoboard-apis';
import { authSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';
import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';
import { BlogTileAtmospheric } from '@/ux/features/website-assembler/blog/BlogTileAtmospheric';

import { useOpenMessage } from '../../hooks/useOpenMessage';
import { ContentRegistry } from '../registries';
import { ContentItemProps } from '../registries/ContentRegistry';

interface BlogMetadata {
  boardId: string;
  name: string;
  description: string;
  displayImage: string;
  accessType: 'NONE' | 'EMAIL' | 'CODE';
  isPasswordRequired: boolean;
  createdBy: string;
  creatorInfo: {
    Name: string;
    Email: string;
    DisplayPic: string;
    Handle: string;
    Headline?: string;
    Id: string;
  };
  isMember: boolean;
  isExclusive: boolean;
  summary: string;
  questions: string[];
  shareId: string;
}

const BlogCard: React.FC<ContentItemProps> = ({ content, variant = 'card', onAction, className }) => {
  const auth = useContextSelector(AppContext, authSelector);
  const isUserLoggedIn = auth.loggedIn;
  const [metadata, setMetadata] = useState<BlogMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { openMessage } = useOpenMessage();

  const isFromWebsite = content.data?.isFromWebsite || content.data?.isRealBlog;
  const needsMetadata = content.data?.needsMetadata;

  // Fetch metadata for website blogs
  useEffect(() => {
    if (isFromWebsite && needsMetadata && content.data?.blogUrl) {
      const blogUrl = content.data.blogUrl;
      const cacheKey = `blog_metadata_${content.id}`;

      // Check cache first
      const cachedData = metadataCache.get(cacheKey);
      if (cachedData) {
        setMetadata(cachedData);
        return;
      }

      setLoading(true);
      setError(null);

      // Use the metadata API endpoint
      getMetaData(null, blogUrl)
        .then((data) => {
          if (data) {
            const blogMetadata: BlogMetadata = {
              boardId: data.boardId || content.id,
              name: data.name || content.title,
              description: data.description || '',
              displayImage: data.displayImage || '',
              accessType: data.accessType || 'NONE',
              isPasswordRequired: data.isPasswordRequired || false,
              createdBy: data.createdBy || '',
              creatorInfo: data.creatorInfo || {
                Name: 'Unknown Author',
                Email: '',
                DisplayPic: '',
                Handle: '',
                Id: '',
              },
              isMember: data.isMember || false,
              isExclusive: data.isExclusive || false,
              summary: data.summary || '',
              questions: data.questions || [],
              shareId: data.shareId || content.id,
            };
            setMetadata(blogMetadata);
            // Cache the result
            metadataCache.set(cacheKey, blogMetadata);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to fetch blog metadata:', err);
          setError('Failed to load blog details');
          setLoading(false);
        });
    }
  }, [isFromWebsite, needsMetadata, content.data?.blogUrl, content.id, content.title]);

  // Convert content to BlogTileAtmospheric format
  const blogItem = {
    id: content.id,
    url: content.url || content.data?.blogUrl || `/blog/${content.id}`,
  };

  // Use fetched metadata if available, otherwise construct from content
  const blogMetadata =
    metadata ||
    (content.data && !needsMetadata
      ? {
          boardId: content.data.boardId,
          name: content.data.name,
          description: content.data.description || '',
          displayImage: content.data.displayImage || '',
          accessType: content.data.accessType || ('NONE' as 'NONE' | 'EMAIL' | 'CODE'),
          isPasswordRequired: content.data.isPasswordRequired || false,
          createdBy: content.data.createdBy || '',
          creatorInfo: content.data.creatorInfo || {
            Name: 'Unknown Author',
            Email: '',
            DisplayPic: '',
            Handle: '',
            Headline: '',
            Id: '',
          },
          isMember: content.data.isMember || false,
          isExclusive: content.data.isExclusive || false,
          summary: content.data.summary || content.description || '',
          questions: content.data.questions || [],
          shareId: content.data.shareId || content.id,
        }
      : null);

  const handleClick = async () => {
    // If we have metadata with boardId, fetch the full board data
    if (blogMetadata?.boardId) {
      try {
        // Fetch full board data
        const boardData = await getBoardDataById(null, { boardId: blogMetadata.boardId });

        if (!boardData) {
          console.error('Failed to fetch board data for boardId:', blogMetadata.boardId);
          // Fallback to original content
          onAction?.('inspect', content);
          return;
        }

        // Transform to immersive content format (same as BlogListWithInspect)
        const immersiveContent: ContentItem = {
          id: boardData._id || content.id,
          type: 'blog',
          title: boardData.name || blogMetadata.name,
          description: boardData.description || blogMetadata.summary || blogMetadata.description,
          media:
            boardData.displayImage || blogMetadata.displayImage
              ? {
                  hero: {
                    url: appendImageRepoDomainIfNotExistInUrl(
                      boardData.displayImage || blogMetadata.displayImage,
                    ),
                    alt: boardData.name || blogMetadata.name,
                  },
                }
              : undefined,
          tags: [],
          categories: ['Blog'],
          engagement: {
            views: 0,
            likes: 0,
            comments: 0,
          },
          temporal: {
            publishedAt: boardData.createdAt,
            createdAt: boardData.createdAt,
          },
          source: {
            author: {
              id: blogMetadata.creatorInfo.Id,
              name: blogMetadata.creatorInfo.Name,
              email: blogMetadata.creatorInfo.Email,
              avatar: blogMetadata.creatorInfo.DisplayPic
                ? appendImageRepoDomainIfNotExistInUrl(blogMetadata.creatorInfo.DisplayPic)
                : undefined,
              role: blogMetadata.creatorInfo.Headline,
              handle: blogMetadata.creatorInfo.Handle,
            },
          },
          data: {
            elements: boardData.elements || [],
            theme: boardData.theme,
            accessType: blogMetadata.accessType,
            isMember: blogMetadata.isMember,
            isExclusive: blogMetadata.isExclusive,
            generatedSummary: blogMetadata.summary,
            questions: blogMetadata.questions,
            shareId: blogMetadata.shareId,
            boardId: blogMetadata.boardId,
          },
        };

        // Trigger the immersive inspect view with proper board data
        onAction?.('inspect', immersiveContent);
      } catch (error) {
        console.error('Error fetching board data:', error);
        // Fallback to original content
        onAction?.('inspect', content);
      }
    } else {
      // Fallback to original content if no metadata
      onAction?.('inspect', content);
    }
  };

  const handleChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    const vendorId = content.data?.vendorId || content.source?.author?.id || metadata?.creatorInfo?.Id;
    const vendorName = content.source?.author?.name || metadata?.creatorInfo?.Name;
    const blogTitle = metadata?.name || content.title || 'this blog post';
    openMessage({
      vendorId,
      vendorName,
      prefilledMessage: `Hi, I just read your blog post "${blogTitle}" and I'd like to discuss it further.`,
      contentContext: content,
    });
  };

  // For minimal variant, we can still use BlogTileAtmospheric but with custom styling
  if (variant === 'minimal') {
    return (
      <div className={`${className} blog-tile-minimal relative`}>
        <BlogTileAtmospheric
          blog={blogItem}
          metadata={blogMetadata}
          onClick={handleClick}
          loading={loading && needsMetadata}
          isUserLoggedIn={isUserLoggedIn}
        />
        <button
          onClick={handleChat}
          className="absolute top-2 right-2 p-2 inspect-theme-button-secondary rounded-lg transition-colors"
          title="Chat about this story"
        >
          <MessageCircle className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // For regular and expanded variants, use BlogTileAtmospheric
  return (
    <div className={`${className} ${variant === 'expanded' ? 'blog-tile-expanded' : ''} relative`}>
      <BlogTileAtmospheric
        blog={blogItem}
        metadata={blogMetadata}
        onClick={handleClick}
        loading={loading && needsMetadata}
        isUserLoggedIn={isUserLoggedIn}
      />
      <button
        onClick={handleChat}
        className="absolute top-2 right-2 p-2 inspect-theme-button-secondary rounded-lg transition-colors"
        title="Chat about this story"
      >
        <MessageCircle className="w-4 h-4" />
      </button>
    </div>
  );
};

// Auto-register with ContentRegistry
ContentRegistry.register('blog', BlogCard);

export default BlogCard;
